export const ClientCategory = { A: 'A', B: 'B', C: 'C' } as const
export type ClientCategory = (typeof ClientCategory)[keyof typeof ClientCategory]

export const RequestType = { TEKUCHKA: 'TEKUCHKA', PROJECT: 'PROJECT' } as const
export type RequestType = (typeof RequestType)[keyof typeof RequestType]

export const RequestStatus = {
  DRAFT: 'DRAFT',
  NEW: 'NEW',
  IN_PROGRESS: 'IN_PROGRESS',
  SENT_TO_PROCUREMENT: 'SENT_TO_PROCUREMENT',
  PARTIALLY_PROCESSED: 'PARTIALLY_PROCESSED',
  QUOTATION_READY: 'QUOTATION_READY',
  QUOTATION_CREATED: 'QUOTATION_CREATED',
  QUOTE_SENT: 'QUOTE_SENT',
  CLOSED_WON: 'CLOSED_WON',
  CLOSED_LOST: 'CLOSED_LOST',
  ARCHIVED: 'ARCHIVED',
} as const
export type RequestStatus = (typeof RequestStatus)[keyof typeof RequestStatus]

export const RequestItemPriority = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const
export type RequestItemPriority = (typeof RequestItemPriority)[keyof typeof RequestItemPriority]

export const RequestItemStatus = {
  NEW: 'NEW',
  IN_QUEUE: 'IN_QUEUE',
  IN_PROGRESS: 'IN_PROGRESS',
  NEEDS_INFO: 'NEEDS_INFO',
  QUOTED: 'QUOTED',
  PARTIAL: 'PARTIAL',
  READY_FOR_QUOTATION: 'READY_FOR_QUOTATION',
  QUOTATION_CREATED: 'QUOTATION_CREATED',
  ORDERED: 'ORDERED',
  // Блок 2 «Заказ»: счёт по позиции подтверждён (заказ перешёл в «Подтверждён»).
  // Параллель ORDERED («Счёт»): ORDERED → ORDER_CONFIRMED при подтверждении
  // заказа. Финальный «выигрышный» статус позиции.
  ORDER_CONFIRMED: 'ORDER_CONFIRMED',
  REJECTED_BY_CUSTOMER: 'REJECTED_BY_CUSTOMER',
  REWORK: 'REWORK',
  // Поставщики ответили «позиции нет» (квоты с ценой 0) — закупка отработала,
  // предложить нечего. Параллель закупщицкому NOT_FOUND.
  NOT_FOUND: 'NOT_FOUND',
  NOT_ACTUAL: 'NOT_ACTUAL',
  CANCELLED: 'CANCELLED',
  // Legacy — оставлены в enum для совместимости со старыми данными, в UI
  // не отображаются в выпадашках. Миграция 20260417000002 переводит их в
  // корректные актуальные статусы.
  WORK_PROCUREMENT: 'WORK_PROCUREMENT',
  WORK_SALES: 'WORK_SALES',
  KP_SENT: 'KP_SENT',
  TO_PROCUREMENT: 'TO_PROCUREMENT',
  INVOICE: 'INVOICE',
  ARCHIVED: 'ARCHIVED',
  NO_OFFER: 'NO_OFFER',
  REFUSED: 'REFUSED',
  NO_RESPONSE: 'NO_RESPONSE',
} as const
export type RequestItemStatus = (typeof RequestItemStatus)[keyof typeof RequestItemStatus]

/**
 * Финальные статусы позиции — после них нет «работы» по позиции.
 * Когда ВСЕ позиции заявки в финале — заявка автоматически закрывается:
 * есть хоть одна ORDERED → CLOSED_WON, иначе → CLOSED_LOST.
 */
export const FINAL_ITEM_STATUSES: ReadonlySet<RequestItemStatus> = new Set([
  'ORDERED',
  'ORDER_CONFIRMED',
  'REJECTED_BY_CUSTOMER',
  'NOT_ACTUAL',
  'CANCELLED',
])

/**
 * «Выигрышные» финальные статусы позиции (клиент согласовал — счёт/заказ).
 * Используется в авто-закрытии заявки: если есть хоть одна такая позиция →
 * заявка закрывается в CLOSED_WON. ORDER_CONFIRMED («Заказ», Блок 2) — наравне
 * с ORDERED («Счёт»).
 */
export const WON_ITEM_STATUSES: ReadonlySet<RequestItemStatus> = new Set([
  'ORDERED',
  'ORDER_CONFIRMED',
])

/** Legacy-статусы позиций — скрыты из выпадашек UI. */
export const LEGACY_ITEM_STATUSES: ReadonlySet<RequestItemStatus> = new Set([
  'WORK_PROCUREMENT',
  'WORK_SALES',
  'KP_SENT',
  'TO_PROCUREMENT',
  'INVOICE',
  'ARCHIVED',
  'NO_OFFER',
  'REFUSED',
])

/** Русские лейблы статусов позиции для UI и логирования. */
export const ITEM_STATUS_LABELS_RU: Record<RequestItemStatus, string> = {
  NEW: 'Новая',
  IN_QUEUE: 'В очереди',
  IN_PROGRESS: 'В работе',
  NEEDS_INFO: 'Нужна инфо',
  QUOTED: 'Квота получена',
  PARTIAL: 'Частично готова',
  READY_FOR_QUOTATION: 'Готова к КП',
  QUOTATION_CREATED: 'КП выставлено',
  ORDERED: 'Счёт',
  ORDER_CONFIRMED: 'Заказ',
  REJECTED_BY_CUSTOMER: 'Отказ клиента',
  REWORK: 'Доработка',
  NOT_FOUND: 'Не найдено',
  NOT_ACTUAL: 'Неактуальна',
  CANCELLED: 'Отменена',
  NO_RESPONSE: 'Нет ответа',
  WORK_PROCUREMENT: 'В работе (legacy)',
  WORK_SALES: 'В работе ОП (legacy)',
  KP_SENT: 'КП отправлено (legacy)',
  TO_PROCUREMENT: 'В закупку (legacy)',
  INVOICE: 'Счёт (legacy)',
  ARCHIVED: 'В архиве',
  NO_OFFER: 'Нет предложения (legacy)',
  REFUSED: 'Отказ клиента (legacy)',
}

/**
 * Русские лейблы статусов заявки для UI, аналитики и логирования.
 * Совпадают с STATUS_LABELS фронта (requestsColDefs) — единый источник для бэка.
 */
export const REQUEST_STATUS_LABELS_RU: Record<RequestStatus, string> = {
  DRAFT: 'Черновик',
  NEW: 'Новая',
  IN_PROGRESS: 'В работе',
  SENT_TO_PROCUREMENT: 'В закупку',
  PARTIALLY_PROCESSED: 'Частично',
  QUOTATION_READY: 'Готова к КП',
  QUOTATION_CREATED: 'КП создано',
  QUOTE_SENT: 'КП отправлено',
  CLOSED_WON: 'Счёт',
  CLOSED_LOST: 'Отказ',
  ARCHIVED: 'В архиве',
}

/** Statuses visible in procurement queue */
export const PROCUREMENT_QUEUE_STATUSES: RequestItemStatus[] = [
  'NEW',
  'IN_QUEUE',
  'IN_PROGRESS',
  'NEEDS_INFO',
  'REWORK',
]

/**
 * Статусы заявок, доступные роли PROCUREMENT (список заявок, детали, комменты,
 * фильтр очереди закупки). Исключены только DRAFT и CLOSED_LOST.
 *
 * Правило Сергея от 10.06.2026 (пересмотр правила от 20.05): «Архив» (ARCHIVED)
 * БОЛЬШЕ не убирает позиции из вида закупщика — позиции архивной заявки должны
 * оставаться видны (квоты не теряются). Позиция пропадает у закупщика ТОЛЬКО
 * при удалении заявки или удалении самой позиции. «Отказ» (CLOSED_LOST)
 * по-прежнему убирает позицию из очереди. QUOTE_SENT и CLOSED_WON остаются
 * видимыми — закупщик может дать ещё лучшую цену даже после КП/заказа.
 *
 * ВАЖНО: единый источник истины. Если нужно изменить — правь здесь, не
 * дублируй в api-модулях.
 */
export const PROCUREMENT_VISIBLE_REQUEST_STATUSES: RequestStatus[] = [
  'NEW',
  'SENT_TO_PROCUREMENT',
  'IN_PROGRESS',
  'PARTIALLY_PROCESSED',
  'QUOTATION_READY',
  'QUOTATION_CREATED',
  'QUOTE_SENT',
  'CLOSED_WON',
  'ARCHIVED',
]

/**
 * «Живые» для закупки статусы заявок (подмножество VISIBLE): работа по
 * квотированию ещё идёт — без QUOTE_SENT/CLOSED_WON/ARCHIVED. Используется
 * KPI «Просрочено» дашборда РОЗ и фильтром `activeOnly` очереди квот (16.07):
 * без этого просрочка считалась по давно закрытым заявкам (2894 из ~3100)
 * и была нерабочей цифрой.
 */
export const PROCUREMENT_ACTIVE_REQUEST_STATUSES: RequestStatus[] = [
  'NEW',
  'SENT_TO_PROCUREMENT',
  'IN_PROGRESS',
  'PARTIALLY_PROCESSED',
  'QUOTATION_READY',
  'QUOTATION_CREATED',
]

/** Priority sort order for procurement queue (lower = higher priority) */
export const PRIORITY_ORDER: Record<RequestItemPriority, number> = {
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
}

/** Category sort order for procurement queue */
export const CATEGORY_ORDER: Record<ClientCategory, number> = {
  A: 1,
  B: 2,
  C: 3,
}

/**
 * Допустимые приоритеты для позиции в зависимости от категории клиента.
 * Ключевые клиенты (A) не могут иметь LOW-приоритет — бизнес-правило.
 */
export function allowedPrioritiesForCategory(category: ClientCategory): RequestItemPriority[] {
  if (category === 'A') return ['HIGH', 'MEDIUM']
  return ['HIGH', 'MEDIUM', 'LOW']
}

/** Проверка: допустима ли пара (категория клиента, приоритет позиции). */
export function isPriorityAllowedForCategory(
  category: ClientCategory,
  priority: RequestItemPriority,
): boolean {
  return allowedPrioritiesForCategory(category).includes(priority)
}

/**
 * Допустимые переходы статуса ПОЗИЦИИ. Одна точка правды для api и web
 * (перенесено из `apps/api/.../request-items.workflow.ts` 13.09.2026, пункт
 * HIGH-6): сервер проверяет переход, фронт по этой же карте показывает в
 * выпадашке только достижимые статусы — раньше карта жила только на сервере,
 * а UI предлагал все статусы подряд.
 *
 * Пустой список = тупик: из статуса нет ручных переходов (финал, автостатус
 * или legacy).
 *
 * С 14.09.2026 карта СТРОГАЯ (решение Сергея, вариант A пункта HIGH-6): сервер
 * отклоняет ручную смену статуса вне карты (`PATCH /request-items/:id` и
 * `PATCH /request-items/bulk-status`), выпадашка статуса показывает только
 * переходы из текущего статуса. Автостатусы и сделочные операции (КП → «Счёт» /
 * «Отказ клиента», снятие сделки, заказы Блока 2) идут своими путями со своими
 * проверками и этой картой не ограничиваются.
 */
export const ITEM_TRANSITIONS: Record<RequestItemStatus, RequestItemStatus[]> = {
  NEW: ['IN_QUEUE'],
  IN_QUEUE: ['IN_PROGRESS', 'NO_RESPONSE'],
  IN_PROGRESS: ['NEEDS_INFO', 'QUOTED', 'PARTIAL', 'REWORK', 'NOT_ACTUAL', 'NO_RESPONSE'],
  NEEDS_INFO: ['IN_PROGRESS'],
  // Возврат «В работу» — быстрый переход очереди закупки (запросить ещё квоты,
  // доработать после «Доработки»); был в UI очереди до включения строгой карты.
  QUOTED: ['READY_FOR_QUOTATION', 'IN_PROGRESS'],
  PARTIAL: ['READY_FOR_QUOTATION', 'IN_PROGRESS'],
  REWORK: ['IN_QUEUE', 'IN_PROGRESS'],
  READY_FOR_QUOTATION: ['QUOTATION_CREATED', 'QUOTED', 'NOT_ACTUAL', 'CANCELLED'],
  QUOTATION_CREATED: ['READY_FOR_QUOTATION', 'NOT_ACTUAL', 'ORDERED', 'REJECTED_BY_CUSTOMER'],
  // Финальные статусы сделки. Можно передумать: ORDERED ↔ REJECTED_BY_CUSTOMER,
  // или вернуть в READY_FOR_QUOTATION (например, для повторного КП).
  // ORDERED → ORDER_CONFIRMED («Счёт» → «Заказ») делает модуль orders (Блок 2)
  // при подтверждении заказа — собственным narrow-update, в обход patchRequestItem.
  ORDERED: ['ORDER_CONFIRMED', 'REJECTED_BY_CUSTOMER', 'READY_FOR_QUOTATION'],
  // Блок 2: «Заказ» — счёт подтверждён. Откат заказа возвращает в ORDERED.
  ORDER_CONFIRMED: ['ORDERED', 'REJECTED_BY_CUSTOMER'],
  REJECTED_BY_CUSTOMER: ['ORDERED', 'READY_FOR_QUOTATION'],
  // Выходы из «Неактуальна» / «Отменена» / «Не найдено» — только обратно
  // «В очереди» (решение Сергея 14.09.2026): закупка увидит позицию заново.
  // Без них ошибочно помеченную позицию нельзя было вернуть в работу.
  NOT_ACTUAL: ['IN_QUEUE'],
  CANCELLED: ['IN_QUEUE'],
  // NOT_FOUND выставляется автостатусом (все поставщики ответили ценой 0);
  // авто-движок и сам вернёт в IN_QUEUE при новом запросе.
  NOT_FOUND: ['IN_QUEUE'],
  // Legacy-статусы: ручных переходов нет, значения живут ради старых записей.
  WORK_PROCUREMENT: [],
  WORK_SALES: [],
  KP_SENT: [],
  TO_PROCUREMENT: [],
  INVOICE: [],
  ARCHIVED: [],
  NO_OFFER: [],
  REFUSED: [],
  NO_RESPONSE: ['IN_QUEUE'],
}
